import type {
  CalendarConnectionDto,
  MeetingConflictDto,
  MeetingDto,
  RescheduleMeetingInput,
  ScheduleMeetingInput,
} from '@ewp/contracts';

import { apiRequest } from '@/shared/api/http-client';

export const meetingsApi = {
  listInRange: (from: Date, to: Date, workspaceId?: string) =>
    apiRequest<MeetingDto[]>('/meetings', {
      query: { from: from.toISOString(), to: to.toISOString(), workspaceId },
    }),

  /** Every meeting with this task on its agenda, past and future. */
  listForItem: (itemId: string) => {
    const year = 365 * 24 * 60 * 60 * 1000;
    return apiRequest<MeetingDto[]>('/meetings', {
      query: {
        from: new Date(Date.now() - year).toISOString(),
        to: new Date(Date.now() + year).toISOString(),
        itemId,
      },
    });
  },

  conflicts: (startsAt: Date, endsAt: Date, attendeeIds: string[], excludeMeetingId?: string) =>
    apiRequest<MeetingConflictDto[]>('/meetings/conflicts', {
      query: {
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        attendeeIds: attendeeIds.join(','),
        excludeMeetingId,
      },
    }),

  schedule: (body: ScheduleMeetingInput) =>
    apiRequest<MeetingDto>('/meetings', { method: 'POST', body }),

  reschedule: (id: string, body: RescheduleMeetingInput) =>
    apiRequest<MeetingDto>(`/meetings/${id}`, { method: 'PATCH', body }),

  /** The whole guest list, not a change to it. Adds and removals are worked
   *  out server-side so the calendar invitations follow. */
  updateAttendees: (id: string, attendeeIds: string[]) =>
    apiRequest<MeetingDto>(`/meetings/${id}/attendees`, {
      method: 'PATCH',
      body: { attendeeIds },
    }),

  cancel: (id: string) => apiRequest<void>(`/meetings/${id}`, { method: 'DELETE' }),

  /** Calls off every remaining occurrence of a repeat. */
  cancelSeries: (id: string) =>
    apiRequest<{ cancelled: number }>(`/meetings/${id}/series`, { method: 'DELETE' }),

  calendarStatus: () => apiRequest<CalendarConnectionDto>('/integrations/google/status'),

  authorizeUrl: () => apiRequest<{ url: string }>('/integrations/google/authorize'),

  disconnectCalendar: () => apiRequest<void>('/integrations/google', { method: 'DELETE' }),
};
